<template>
  <div>
    <v-card>
      <v-toolbar dense color="grey lighten-3" flat>
        <v-toolbar-title>Copy Policy</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn text icon @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text>
        <v-container fluid grid-list-sm>
          <v-layout row wrap>
            <v-flex xs4 v-if="policyData">
              <v-text-field v-model="policyData.polNo" :label="labels.polNo" disabled></v-text-field>
            </v-flex>
            <v-flex xs4 v-if="policyData">
              <v-text-field v-model="policyData.renCnt" :label="labels.renCnt" disabled></v-text-field>
            </v-flex>
            <v-flex xs4 v-if="policyData">
              <v-text-field v-model="policyData.endtCnt" :label="labels.endtCnt" disabled></v-text-field>
            </v-flex>

            <v-flex xs4>
              <cal-picker
                v-model="forms.comDate"
                :label="labels.comDate"
                v-validate="comDateValidation"
                :data-vv-as="labels.comDate"
                data-vv-name="comDate"
                :error-messages="errors.collect('comDate')"
                @input="doComDate"
                :loading="dateLoading"
              ></cal-picker>
              <warning v-model="warnComDate" @ok="doAcceptComDate" @cancel="doCancelComDate">{{ warnComDateMsg }}</warning>
            </v-flex>
            <v-flex xs4>
              <cal-picker
                v-model="forms.expDate"
                :label="labels.expDate"
                v-validate="expDateValidation"
                :data-vv-as="labels.expDate"
                data-vv-name="expDate"
                :error-messages="errors.collect('expDate')"
                :hint="daysCoverage"
                :loading="dateLoading"
                :disabled="polType.perBancBill"
                @input="doExpDate"
              ></cal-picker>
              <warning v-model="warnExpDate" @ok="doAcceptExpDate">{{ warnExpDateMsg }}</warning>
            </v-flex>
            <v-flex xs4 v-if="polType.comDateOpt === 'O'">
              <v-text-field
                v-model="forms.uwYr"
                :label="labels.uwYr"
                :error-messages="errors.collect('uwYr')"
                data-vv-name="uwYr"
                :data-vv-as="labels.uwYr"
                v-int-mask="{ intScale: 4 }"
                type="tel"
                :loading="dateLoading"
              ></v-text-field>
            </v-flex>
            <v-flex xs4 v-else>
              <v-text-field
                v-model="forms.uwYr"
                :label="labels.uwYr"
                :error-messages="errors.collect('uwYr')"
                data-vv-name="uwYr"
                :data-vv-as="labels.uwYr"
                v-validate="'required|max:4'"
                readonly
                :loading="dateLoading"
              ></v-text-field>
            </v-flex>

            <v-flex xs12>
              <v-expansion-panels v-model="panel" multiple>
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <div><v-icon>mdi-filter-variant</v-icon> {{ labels.exclude }}</div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-card>
                      <v-card-text>
                        <v-container fluid grid-list-sm>
                          <v-layout row wrap>
                            <v-flex xs12>
                              <v-checkbox color="primary" :label="labels.exclReins" v-model="forms.exclReins"></v-checkbox>
                            </v-flex>
                            <v-flex xs12>
                              <v-checkbox color="primary" :label="labels.exclClauses" v-model="forms.exclClauses"></v-checkbox>
                            </v-flex>
                            <v-flex xs12>
                              <v-checkbox color="primary" :label="labels.exclDocTxt" v-model="forms.exclDocTxt"></v-checkbox>
                            </v-flex>
                          </v-layout>
                        </v-container>
                      </v-card-text>
                    </v-card>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-flex>
          </v-layout>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn depressed @click="$emit('close')"
          ><span class="btn-normal-error">{{ labels.close }}</span></v-btn
        >
        <v-btn depressed @click="doProceed"
          ><span class="btn-normal-primary">{{ labels.proceed }}</span></v-btn
        >
      </v-card-actions>
    </v-card>

    <v-dialog v-model="loading" persistent width="300">
      <v-card color="primary" dark>
        <v-card-text class="pa-3">
          Processing...
          <v-progress-linear indeterminate color="white" class="mb-0"></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showErrorDialog" width="800px">
      <v-card>
        <v-toolbar dense color="grey lighten-3" flat>
          <v-icon>mdi-information</v-icon>
          <v-toolbar-title>System Information</v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>
        <v-card-text>
          <div v-if="messages.length > 0" class="pb-3">
            <v-subheader>Oops something went wrong:</v-subheader>
            <ul class="showErrorMsgs">
              <li v-for="(error, index) in messages" :key="index">
                {{ error.message }}
              </li>
            </ul>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red darken-1" text @click.native="showErrorDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { expandURI } from '@/utils/ajax'
import differenceInDays from 'date-fns/differenceInDays'
import { toDate } from 'date-fns'
import addYears from 'date-fns/addYears'
import subDays from 'date-fns/subDays'
import addMonths from 'date-fns/addMonths'
import MessageMixin from '@/mixins/message'

export default {
  data() {
    return {
      labels: {
        polNo: 'Policy No.',
        renCnt: 'Renewal Count',
        endtCnt: 'Endorsement Count',
        comDate: 'Commencement Date',
        expDate: 'Expiry Date',
        uwYr: 'Underwriting Year',
        exclude: 'Exclude (Optional)',
        exclReins: 'Reinsurance',
        exclClauses: 'Clauses Text',
        exclDocTxt: 'Document Text (Upper/Lower/Memo)',
        close: 'Close',
        proceed: 'Proceed'
      },
      forms: {
        polNo: undefined,
        renCnt: undefined,
        endtCnt: undefined,
        comDate: undefined,
        expDate: undefined,
        uwYr: undefined,
        exclReins: undefined,
        exclClauses: undefined,
        exclDocTxt: undefined
      },
      policyGeneralParam: {
        startUWYr: undefined,
        add1UwYr: undefined
      },
      polType: {
        comDateOpt: undefined,
        polNoForm: undefined,
        perBancBill: undefined,
        expiryTime: undefined
      },
      dateLoading: false,
      warnComDate: false,
      acceptComDate: false,
      warnComDateMsg: undefined,
      warnExpDate: false,
      acceptExpDate: false,
      warnExpDateMsg: undefined,
      loading: false,
      showErrorDialog: false,
      panel: [0]
    }
  },
  props: {
    policyData: Object
  },
  computed: {
    comDateValidation() {
      if (this.polType.comDateOpt === 'M') {
        return 'required'
      } else if (this.polType.comDateOpt === 'N') {
        return 'required_blank'
      } else {
        return ''
      }
    },
    expDateValidation() {
      if (this.forms.comDate === undefined || this.forms.comDate === '') {
        return 'required_blank'
      } else if (this.forms.comDate !== undefined && this.forms.comDate !== '') {
        return 'required|start_enddate:' + this.forms.comDate
      } else {
        return ''
      }
    },
    daysCoverage() {
      if (this.forms.comDate && this.forms.expDate) {
        if (this.forms.expDate === undefined || this.forms.comDate === undefined) {
          return undefined
        } else {
          return 'Days Covered: ' + (differenceInDays(toDate(this.forms.expDate), toDate(this.forms.comDate)) + 1)
        }
      } else {
        return undefined
      }
    },
    displayErrorDialog() {
      return this.messages
    }
  },
  watch: {
    policyData: function (value) {
      if (value) {
        this.doInit()
      }
    },
    displayErrorDialog: {
      handler: function (messages) {
        if (messages && messages.length > 0) {
          this.showErrorDialog = true
        }
      },
      deep: true
    }
  },
  methods: {
    async doInit() {
      await this.getPolicyParam()
      this.getPolType()
    },
    async getPolicyParam() {
      const apiUrl = this.$store.state.underwriting.menu.policyGeneral
      if (apiUrl) {
        try {
          const result = await this.$http.get(apiUrl)
          this.policyGeneralParam.startUWYr = result.data.startUWYr
          this.policyGeneralParam.add1UwYr = result.data.add1UwYr
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.notify(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async getPolType() {
      const apiUrl = this.$store.state.underwriting.policyGeneralSettings.menu.polType
      if (apiUrl) {
        try {
          this.dateLoading = true
          const expand = {
            polType: this.policyData.polType
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          if (result) {
            const { data } = result
            this.polType.expiryTime = data.expiryTime
            this.polType.comDateOpt = data.comDateOpt
            if (data.comDateOpt === 'M') {
              this.forms.comDate = new Date()
            } else {
              this.forms.comDate = undefined
              this.forms.expDate = undefined
            }
            this.doComDate()
            this.polType.polNoForm = data.polNoForm
            this.polType.perBancBill = data.perBancBill
            // this.doShowPolNo()
            this.dateLoading = false
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.notify(data)
          }
          this.dateLoading = false
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    doComDate() {
      this.doWarnComDate()
      this.getDefUWYR()
      this.getDefExpDate()
    },
    doWarnComDate() {
      if (!this.acceptComDate) {
        var nodays = differenceInDays(toDate(new Date()), toDate(this.forms.comDate)) + 1
        if (nodays < -60) {
          this.warnComDateMsg = '10005 – This is more than 60 days later than Today. (10005)'
          this.warnComDate = true
          return false
        } else if (nodays > 60) {
          this.warnComDateMsg = '10004 – This is more than 60 days earlier than Today. (10004)'
          this.warnComDate = true
          return false
        }
      }
      return true
    },
    doAcceptComDate() {
      this.acceptComDate = true
    },
    doCancelComDate() {
      this.forms.comDate = undefined
      this.doComDate()
    },
    doWarnExpDate() {
      if (
        !this.acceptExpDate &&
        (this.forms.expDate === undefined || this.forms.expDate === '') &&
        this.forms.comDate !== undefined &&
        this.forms.comDate !== ''
      ) {
        this.warnExpDateMsg = '10006 – Expiry Date cannot be blank when Commencement Date entered. (10006)'
        this.warnExpDate = true
        return false
      }
      return true
    },
    doAcceptExpDate() {
      this.acceptExpDate = true
    },
    doExpDate() {
      this.doWarnExpDate()
    },
    getDefUWYR() {
      if (this.forms.comDate === undefined || this.forms.comDate === '') {
        this.forms.uwYr = new Date().getFullYear()
      } else {
        var iMonth = (this.forms.comDate.getMonth() + 1).toString().padStart(2, '0')
        var iDay = this.forms.comDate.getDate().toString().padStart(2, '0')
        var iMonthDay = iMonth + iDay

        var iAdd1UwYr = 0
        if (this.policyGeneralParam.add1UwYr) {
          iAdd1UwYr = 1
        }
        if (parseInt(iMonthDay) >= parseInt(this.policyGeneralParam.startUWYr)) {
          this.forms.uwYr = this.forms.comDate.getFullYear() + iAdd1UwYr
        } else {
          this.forms.uwYr = this.forms.comDate.getFullYear() - 1 + iAdd1UwYr
        }
      }
    },
    getDefExpDate() {
      if (this.forms.comDate) {
        if (!this.policyData.billType || this.policyData.billType.length === 0 || this.policyData.billType === 'A') {
          let comDateYear = this.forms.comDate.getFullYear()
          if (this.forms.comDate.getMonth() === 1 && this.forms.comDate.getDate() === 29) {
            this.forms.expDate = new Date(comDateYear + 1, 1, 28)
          } else {
            // Add 1 year to end Date
            let expDate = addYears(this.forms.comDate, 1)
            if (this.polType.expiryTime === 2400) {
              expDate = subDays(expDate, 1)
            }
            this.forms.expDate = expDate
          }
        } else if (this.policyData.billType === 'M') {
          let expDate = addMonths(this.forms.comDate, 1)
          if (this.polType.expiryTime === 2400) {
            expDate = subDays(expDate, 1)
          }
          this.forms.expDate = expDate
        } else if (this.policyData.billType === 'T') {
          let expDate = addMonths(this.forms.comDate, 4)
          if (this.polType.expiryTime === 2400) {
            expDate = subDays(expDate, 1)
          }
          this.forms.expDate = expDate
        } else if (this.policyData.billType === 'Q') {
          let expDate = addMonths(this.forms.comDate, 3)
          if (this.polType.expiryTime === 2400) {
            expDate = subDays(expDate, 1)
          }
          this.forms.expDate = expDate
        }
      }
    },
    async doValidateAllWarn() {
      if (!this.acceptComDate) {
        if (!this.doWarnComDate()) {
          return false
        }
      }
      if (!this.acceptExpDate) {
        if (!this.doWarnExpDate()) {
          return false
        }
      }
      return true
    },
    doProceed() {
      this.resetMessages()
      this.doValidateAllWarn().then((result2) => {
        if (result2) {
          this.$validator.validateAll().then((result) => {
            if (result) {
              this.doCopy()
            } else {
              this.promptFieldCheck()
            }
          })
        }
      })
    },
    async doCopy() {
      const apiUrl = this.$store.state.underwriting.policyGeneralSettings.menu.copyPolicy
      if (apiUrl) {
        try {
          this.loading = true
          const expand = {
            polNo: this.policyData.polNo,
            renCnt: this.policyData.renCnt,
            endtCnt: this.policyData.endtCnt,
            comDate: this.forms.comDate,
            expDate: this.forms.expDate,
            uwYr: this.forms.uwYr,
            exclReins: this.formatBoolean(this.forms.exclReins),
            exclClauses: this.formatBoolean(this.forms.exclClauses),
            exclDocTxt: this.formatBoolean(this.forms.exclDocTxt)
          }
          const result3 = await this.$http.post(expandURI(apiUrl, expand))
          if (result3) {
            const item = {
              polNo: result3.data.polNo,
              renCnt: result3.data.renCnt,
              endtCnt: result3.data.endtCnt
            }
            const data = {
              item: item
            }
            this.$store.dispatch('reviewPolicy', data).then(() => {
              this.$router.push({ name: 'policyGeneral' })
              this.loading = false
            })
          } else {
            this.loading = false
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
          return false
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    formatBoolean(value) {
      if (value) {
        return true
      }
      return false
    }
  },
  beforeMount() {
    if (this.$store.state.underwriting.policyGeneralSettings.init !== 1) {
      this.$store.dispatch('policyGeneralInit')
    }
  },
  mixins: [MessageMixin]
}
</script>

<style>
</style>
